import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Textarea,
  useDisclosure,
  Menu as ChakraMenu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { useContext, useMemo } from 'react';
import { GlobalContext } from 'providers/Global';
import { SendTokensData } from '../types';
import { isValidAddress } from 'utils/general';
import { ReactComponent as AddressBookIcon } from 'assets/address-book-icon.svg';
import AddressBookPickerModal from 'components/Modals/AddressBookPickerModal';
import TokenIcon from 'components/TokenIcon';

interface SendTokensFormProps {
  data: SendTokensData;
  onChange: (data: SendTokensData) => void;
}

const dropdownButtonStyle = {
  border: '1px solid',
  borderColor: 'inherit',
  cursor: 'pointer',
  bg: 'white',
  transition: 'all 0.15s',
  _hover: { borderColor: 'blackAlpha.400' },
};

const SendTokensForm = ({ data, onChange }: SendTokensFormProps) => {
  const { balance, currentAccount } = useContext(GlobalContext);
  const { isOpen: isPickerOpen, onOpen: onPickerOpen, onClose: onPickerClose } = useDisclosure();

  const tokens = useMemo(() => currentAccount.tokens || [], [currentAccount.tokens]);

  const handleChange = (field: keyof SendTokensData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const handleTokenSelect = (tokenValue: string) => {
    if (tokenValue === 'HBAR') {
      onChange({
        ...data,
        selectedToken: 'HBAR',
        tokenDecimals: 8,
        tokenSymbol: 'HBAR',
        amount: '',
      });
    } else {
      const token = tokens.find(t => t.token_id === tokenValue);
      if (token) {
        onChange({
          ...data,
          selectedToken: token.token_id,
          tokenDecimals: token.decimals,
          tokenSymbol: token.symbol,
          amount: '',
        });
      }
    }
  };

  const availableBalance = useMemo(() => {
    if (data.selectedToken === 'HBAR') {
      return balance;
    }
    const token = tokens.find(t => t.token_id === data.selectedToken);
    if (!token) return 0;
    // Use string-based division to avoid floating-point precision issues
    const balStr = BigInt(token.balance).toString();
    const dec = token.decimals;
    if (dec === 0) return token.balance;
    const padded = balStr.padStart(dec + 1, '0');
    const intPart = padded.slice(0, padded.length - dec);
    const decPart = padded.slice(padded.length - dec);
    return parseFloat(`${intPart}.${decPart}`);
  }, [data.selectedToken, balance, tokens]);

  const selectedLabel = useMemo(() => {
    if (data.selectedToken === 'HBAR') return 'HBAR';
    const token = tokens.find(t => t.token_id === data.selectedToken);
    return token ? `${token.symbol} - ${token.name}` : data.selectedToken;
  }, [data.selectedToken, tokens]);

  const isAddressInvalid = data.address !== '' && !isValidAddress(data.address);
  const isAmountInvalid = data.amount !== '' && (isNaN(Number(data.amount)) || Number(data.amount) <= 0);
  const notEnoughBalance = data.amount !== '' && Number(data.amount) > availableBalance;

  return (
    <Flex direction="column" gap={4} w="full">
      {/* Token Selector */}
      <Box>
        <Text mb={2} fontWeight={500}>Token</Text>
        <ChakraMenu placement="bottom-start" matchWidth>
          <MenuButton
            as={Box}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            h={12}
            px={4}
            borderRadius={16}
            {...dropdownButtonStyle}
          >
            <Flex alignItems="center" gap={2}>
              <TokenIcon
                symbol={data.tokenSymbol}
                size={20}
                tokenId={data.selectedToken}
              />
              <Text fontSize="sm" fontWeight="500">{selectedLabel}</Text>
            </Flex>
          </MenuButton>
          <MenuList py={2} borderRadius={12} boxShadow="lg" zIndex={10} maxH="240px" overflowY="auto">
            <MenuItem
              fontSize="sm"
              py={2}
              fontWeight={data.selectedToken === 'HBAR' ? '600' : '400'}
              bg={data.selectedToken === 'HBAR' ? 'blackAlpha.50' : 'transparent'}
              _hover={{ bg: 'blackAlpha.100' }}
              onClick={() => handleTokenSelect('HBAR')}
            >
              <Flex alignItems="center" gap={2}>
                <TokenIcon symbol="HBAR" size={20} tokenId="HBAR" />
                HBAR
              </Flex>
            </MenuItem>
            {tokens.map((token) => (
              <MenuItem
                key={token.token_id}
                fontSize="sm"
                py={2}
                fontWeight={data.selectedToken === token.token_id ? '600' : '400'}
                bg={data.selectedToken === token.token_id ? 'blackAlpha.50' : 'transparent'}
                _hover={{ bg: 'blackAlpha.100' }}
                onClick={() => handleTokenSelect(token.token_id)}
              >
                <Flex alignItems="center" gap={2}>
                  <TokenIcon symbol={token.symbol} size={20} tokenId={token.token_id} />
                  {token.symbol} - {token.name}
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </ChakraMenu>
      </Box>

      <Box>
        <Text mb={2} fontWeight={500}>Recipient Address</Text>
        <InputGroup>
          <Input
            h={12}
            borderRadius={16}
            placeholder="0.0.xxxxx"
            value={data.address}
            isInvalid={isAddressInvalid}
            onChange={(e) => handleChange('address', e.target.value)}
          />
          <InputRightElement h={12} w={12}>
            <Box
              as="button"
              title="Select from Address Book"
              cursor="pointer"
              fontSize="18px"
              onClick={onPickerOpen}
            >
              <AddressBookIcon />
            </Box>
          </InputRightElement>
        </InputGroup>
        {isAddressInvalid && (
          <Text color="red.500" size="xs" mt={1}>
            Invalid Hedera account ID
          </Text>
        )}
      </Box>

      <Box>
        <Text mb={2} fontWeight={500}>Amount ({data.tokenSymbol})</Text>
        <Input
          h={12}
          borderRadius={16}
          type="number"
          placeholder="0.00"
          value={data.amount}
          isInvalid={isAmountInvalid || notEnoughBalance}
          onChange={(e) => handleChange('amount', e.target.value)}
        />
        <Text size="xs" color="blackAlpha.500" mt={1}>
          Available: {availableBalance} {data.tokenSymbol}
        </Text>
        {notEnoughBalance && (
          <Text color="red.500" size="xs" mt={1}>
            Insufficient balance
          </Text>
        )}
        {isAmountInvalid && !notEnoughBalance && (
          <Text color="red.500" size="xs" mt={1}>
            Please enter a valid amount
          </Text>
        )}
      </Box>

      <Box>
        <Text mb={2} fontWeight={500}>Description (Optional)</Text>
        <Textarea
          borderRadius={16}
          placeholder="What is this transaction for?"
          value={data.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={2}
        />
      </Box>

      <AddressBookPickerModal
        isOpen={isPickerOpen}
        onClose={onPickerClose}
        onSelect={(address) => handleChange('address', address)}
      />
    </Flex>
  );
};

export default SendTokensForm;
